<template>
  <div>
    <p class="title text-lg mt-6 capitalize">{{ providerDisplay }} Plan Change Offer Details</p>
    <p class="subtitle mt-2 leading-6">
      For when you want to offer customers different plans as an alternative to a coupon or pause. Control the way you discuss your other plans with
      simple nicknames, descriptions, and up to four features for each.
    </p>
    <div class="grid grid-cols-2 gap-2 py-6 border-brand-sand border-b-2" v-for="(planSetting, planIndex) in planSettings" :key="planIndex">
      <div>
        <p class="text-sm font-semibold mb-2 text-gray-700">Subscription Plan</p>
        <!-- Plan Value Options -->
        <el-select
          filterable
          class="brand-input w-full"
          :multiple="false"
          :value="planSetting.planId"
          @change="(planId) => updatePlanSetting(planIndex, { planId })"
        >
          <el-option-group v-if="groupedPriceOptions.active.length" label="Current">
            <el-option
              v-for="plan in groupedPriceOptions.active"
              :key="plan.id.toString()"
              :label="priceDescription(plan)"
              :value="plan.id.toString()"
            >
              <div class="inline-block w-full">
                <div class="flex justify-between items-center pt-2">
                  <p class="mr-4 flex-grow text-brand-blue font-medium text-xs">
                    {{ priceDescription(plan) }}
                  </p>
                  <p class="text-gray-600 text-xs mr-6">
                    {{ plan.nickname || plan.id }}
                  </p>
                </div>
              </div>
            </el-option>
          </el-option-group>
        </el-select>
        <div class="mt-2" v-if="planSetting.planId">
          <div class="flex items-center space-x-1.5 text-brand-brown text-sm mt-2">
            <font-awesome-icon :icon="['fas', 'times-circle']" class="text-sm"></font-awesome-icon>
            <p class="text-sm cursor-pointer font-semibold" @click="removePlanSetting(planIndex)">Remove plan details</p>
          </div>
        </div>
        <!-- Preview of how this plan will appear to customers -->
        <div class="mt-4">
          <div class="px-6 rounded-lg py-4 space-y-2 flex flex-col max-w-xs border-2" :style="{ borderColor: brandColorMiddle }">
            <p class="text-client-primary text-sm font-bold flex items-center space-x-1">
              <span>{{ planSetting.nick || priceDescription({ id: planSetting.planId }).split('for')[0] }} (Preview)</span>
            </p>
            <p class="text-3xl font-bold space-x-1.5">
              <span v-if="planSetting.msrp" class="line-through text-gray-400">{{ planSetting.msrp }}</span>
              <span v-if="planSetting.priceOverride" class="text-brand-black text-opacity-90">{{ planSetting.priceOverride }}</span>
              <span v-else class="text-brand-black text-opacity-90" v-html="priceString({ id: planSetting.planId })"></span>
            </p>
            <p class="text-brand-black text-opacity-80 text-sm font-medium flex items-center space-x-1" v-if="planSetting.tagline">
              {{ planSetting.tagline }}
            </p>
            <div v-if="planSetting.features && planSetting.features.length">
              <p class="text-client-primary text-xs font-semibold my-2 flex items-center space-x-1 tracking-wider">
                <span class="uppercase">Highlights</span>
              </p>
              <div class="space-y-0.5">
                <p
                  class="text-brand-black text-sm font-medium text-opacity-90 flex items-center space-x-1"
                  v-for="(feature, index) in planSetting.features"
                  :key="planSetting.id + 'feature' + index"
                >
                  {{ feature }}
                </p>
              </div>
            </div>
            <div class="pt-2">
              <button
                :style="{ background: brandColor }"
                type="button"
                class="text-center py-2.5 text-sm font-bold rounded-md text-white hover:opacity-75 transition ease-in-out duration-150 w-full mx-auto"
              >
                Switch Plan
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="brand-input px-2 space-y-4">
        <div>
          <p class="text-sm mb-2 font-semibold">Customer-Facing Nickname</p>
          <el-input
            :value="planSetting.nick"
            @input="(nick) => updatePlanSetting(planIndex, { nick })"
            placeholder="Secret Pro Plan"
            maxlength="40"
            show-word-limit
          ></el-input>
        </div>
        <div>
          <p class="text-sm mb-2 font-semibold">Tagline</p>
          <el-input
            :value="planSetting.tagline"
            @input="(tagline) => updatePlanSetting(planIndex, { tagline })"
            placeholder="For professional copywriters"
            maxlength="60"
            show-word-limit
          ></el-input>
        </div>
        <div class="grid grid-cols-2 space-x-2">
          <div>
            <p class="text-sm mb-2 font-semibold">MSRP (optional)</p>
            <el-input
              :value="planSetting.msrp"
              @input="(msrp) => updatePlanSetting(planIndex, { msrp })"
              placeholder="$99.99"
              maxlength="12"
              show-word-limit
            ></el-input>
          </div>
          <div>
            <p class="text-sm mb-2 font-semibold">Price Override (optional)</p>
            <el-input
              :value="planSetting.priceOverride"
              @input="(priceOverride) => updatePlanSetting(planIndex, { priceOverride })"
              placeholder="$79.99"
              maxlength="12"
              show-word-limit
            ></el-input>
          </div>
        </div>
        <div>
          <p class="text-sm mb-2 font-semibold">Feature List</p>
          <div v-for="(feature, featureIndex) in planSetting.features" class="mb-3 relative pr-5" :key="featureIndex + 'featureIndex' + planIndex">
            <el-input
              :value="feature"
              @input="(val) => updatePlanFeature(planIndex, featureIndex, val)"
              placeholder="1,000 Email Credits"
              maxlength="60"
              show-word-limit
            ></el-input>
            <button @click="removePlanFeature(planIndex, featureIndex)" class="absolute -right-3 top-2.5">
              <img src="@/assets/img/cancel-circle.png" alt="Cancel Feature" />
            </button>
          </div>
          <div class="mt-2" v-if="planSetting.features.length < featuresLimitPerPlan">
            <p class="text-brand-blue font-semibold cursor-pointer hover:text-brand-blue text-sm" @click="addPlanFeature(planIndex)">
              + Add another line...
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="grid grid-cols-2 gap-2">
      <div class="mt-6 border-brand-sand">
        <p class="text-sm font-semibold text-gray-700 mb-2">
          {{ planSettings.length ? 'Add a Subscription Plan' : 'Subscription Plan' }}
        </p>
        <el-select filterable class="brand-input w-full" :multiple="false" value="" @change="(planId) => addPlanSetting(planId)">
          <el-option-group v-if="groupedPriceOptions.active.length" label="Current">
            <el-option
              v-for="plan in groupedPriceOptions.active"
              :key="plan.id"
              :label="priceDescription(plan)"
              :value="plan.id"
              :disabled="!!planSettings.find((d) => d.planId === plan.id)"
            >
              <div class="inline-block w-full">
                <div class="flex justify-between items-center pt-2">
                  <p class="mr-4 flex-grow text-brand-blue font-medium text-xs">
                    {{ priceDescription(plan) }}
                  </p>
                  <p class="text-gray-600 text-xs mr-6">
                    {{ plan.nickname || plan.id }}
                  </p>
                </div>
              </div>
            </el-option>
          </el-option-group>
        </el-select>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { FETCH_ORG_PRICES, PLAN_SETTINGS_SCHEDULE_SAVE } from '@/store/action-types';
import { mapGetters } from 'vuex';
import { SET_ORG_DETAILS } from '../../store/mutation-types';

export default Vue.extend({
  name: 'OrgPlanEditor',
  data() {
    return {
      loading: true,
      featuresLimitPerPlan: 8,
      savingProviderSettings: false,
    };
  },
  computed: {
    ...mapGetters(['orgPlanSettings', 'brandColor', 'brandColorLight', 'brandColorMiddle']),
    provider() {
      return this.$store.getters.providerWithVersion; // discerns 'paddle' and 'paddle_billing'
    },
    providerDisplay() {
      return this.$store.getters.provider; // Both paddle versions return 'paddle'
    },
    priceOptions() {
      return this.$store.state.stripeData.prices || [];
    },
    groupedPriceOptions() {
      return {
        active: this.priceOptions.filter((p) => p.active),
        inactive: this.priceOptions.filter((p) => !p.active),
      };
    },
    planSettings() {
      return this.orgPlanSettings || [];
    },
  },
  async created() {
    await this.$store.dispatch(FETCH_ORG_PRICES);
    this.loading = false;
  },
  methods: {
    priceDescription(price) {
      return this.$store.getters.stripePriceDescription(price.id, false);
    },
    priceString(price) {
      return this.$store.getters.stripePriceString(price.id);
    },
    setPlanSettings(plans) {
      this.$store.commit(SET_ORG_DETAILS, { plans });
    },
    scheduleSave() {
      this.$store.dispatch(PLAN_SETTINGS_SCHEDULE_SAVE);
    },
    updatePlanSetting(planIndex, update) {
      const newPlanSettings = this.planSettings.map((p, i) => {
        if (i === planIndex) {
          return { ...p, ...update };
        }
        return p;
      });
      this.setPlanSettings(newPlanSettings);
      this.scheduleSave();
    },
    addPlanSetting(planId) {
      const newItem = {
        planId,
        nick: '',
        tagline: '',
        features: [''],
        msrp: '',
      };
      const newPlanSettings = [...this.planSettings, newItem];
      this.setPlanSettings(newPlanSettings);
      this.scheduleSave();
    },
    removePlanSetting(planIndex) {
      const newPlanSettings = this.planSettings.filter((p, i) => i !== planIndex);
      this.setPlanSettings(newPlanSettings);
      this.scheduleSave();
    },
    updatePlanFeature(planIndex, featureIndex, value) {
      const newFeatures = this.planSettings[planIndex].features.map((f, i) => {
        if (i === featureIndex) {
          return value;
        }
        return f;
      });
      this.updatePlanSetting(planIndex, { features: newFeatures });
    },
    addPlanFeature(planIndex) {
      const newFeatures = [...this.planSettings[planIndex].features, ''];
      this.updatePlanSetting(planIndex, { features: newFeatures });
    },
    removePlanFeature(planIndex, featureIndex) {
      const newFeatures = this.planSettings[planIndex].features.filter((f, i) => i !== featureIndex);
      this.updatePlanSetting(planIndex, { features: newFeatures });
    },
  },
});
</script>
